import { Controller } from "@hotwired/stimulus"

// Inbox — the Messenger two-pane responsive collapse for the operator
// conversations shell. Desktop (md+) shows rail + pane side by side always (pure
// CSS). Mobile is single-pane: the rail by default, the thread full-screen once
// a conversation is active. The server renders the correct initial state
// (index = rail; show = pane) via `active`; this controller only handles the
// MOBILE transition when the pane frame swaps IN PLACE (a rail-row click, which
// updates only the frame contents, not the wrapper's responsive classes).
//
// Targets: rail (the left column), pane (the right frame).
// Value: active (did the server render with a thread open?).
export default class extends Controller {
  static targets = ["rail", "pane"]
  static values = { active: Boolean }

  connect() {
    // React to the pane frame finishing a navigation (a rail-row click on
    // mobile) so we can show the thread and hide the rail. On md+ both are
    // visible regardless, so these class flips are inert there.
    this.onFrameLoad = this.showPane.bind(this)
    if (this.hasPaneTarget) {
      this.paneTarget.addEventListener("turbo:frame-load", this.onFrameLoad)
    }
  }

  disconnect() {
    if (this.hasPaneTarget && this.onFrameLoad) {
      this.paneTarget.removeEventListener("turbo:frame-load", this.onFrameLoad)
    }
  }

  // A thread loaded into the pane: on MOBILE, show the pane full-screen and hide
  // the rail (and vice-versa when the empty pane comes back via the thread's back
  // arrow). On md+ both panes are always visible via pure CSS — so we no-op there
  // to avoid fighting the responsive `md:flex` classes (toggling `hidden`/`flex`
  // by hand would otherwise strip the desktop layout).
  showPane() {
    if (!this.isMobile()) return

    const hasThread = this.paneTarget.querySelector("[data-controller~='thread']")
    if (hasThread) {
      this.railTarget.classList.add("hidden")
      this.railTarget.classList.remove("flex")
      this.paneTarget.classList.remove("hidden")
      this.paneTarget.classList.add("flex")
    } else {
      // Back to the list (empty pane): show the rail, hide the pane on mobile.
      this.railTarget.classList.remove("hidden")
      this.railTarget.classList.add("flex")
      this.paneTarget.classList.add("hidden")
      this.paneTarget.classList.remove("flex")
    }
  }

  isMobile() {
    return window.matchMedia("(max-width: 767px)").matches
  }
}
